import React, { useEffect, useRef, useState } from "react";
import SetlistSealedArt from "./SetlistSealedArt";
import { createPortal } from "react-dom";
import { setlistsApi } from "../services/setlists";
import type { PreviewResponse, PreviewSession, DeliverResponse } from "../types/setlists";
import SetlistTrackReview from "./SetlistTrackReview";
import { useSetlistReview } from "../hooks/useSetlistReview";
import { formatTxnAmount } from "../utils/currency";
import { openExternal } from "../lib/openExternal";
import "./SetlistUploadFlow.css";
import { copyTextToClipboard } from "../lib/clipboard";
import { formatBytes } from "../utils/formatBytes";
import { publicUrl } from "../utils/publicUrl";
import { t as tx } from "../i18n";
import { T } from "../i18n/T";

// Sell Playlists — the delivery flow. One upload clears every waiting sale:
//   pick   → the DJ drops their track-history export (Serato/rekordbox/…)
//   review → POST /setlists/preview parsed + Spotify-matched it; the shared
//            SetlistTrackReview lists every song (✕ removes, Confirm or Swap
//            the unsure ones, + adds songs the file doesn't have)
//   done   → POST /setlists/deliver captured the sales, emailed the fans.
//
// The reviewed tracklist is authoritative (the DJ is the author) — the server
// re-guards only the money side.

const ACCEPTED_EXTENSIONS = [".csv", ".txt", ".xml", ".m3u", ".m3u8"];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(",");
const MAX_BYTES = 8 * 1024 * 1024; // matches the server's multer limit

// "DJ Jax'on - August 15 Setlist" from the session's own date; today as
// fallback. Cosmetic parity with the server's buildSetlistName (founder
// format: DJ first, full month, no weekday) — the server recomposes
// the authoritative name at /deliver either way, so even a stale bundle
// can't ship the old format.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function buildDefaultName(session: PreviewSession | null, djName?: string): string {
  const src = String(session?.startedAt || session?.dateLabel || "").replace(/ /g, " ");
  const m = src.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  let month: string | null = null;
  let day = 0;
  if (m) {
    month = MONTH_NAMES[Number(m[1]) - 1] || null;
    day = Number(m[2]);
  }
  if (!month || day < 1 || day > 31) {
    const now = new Date();
    month = MONTH_NAMES[now.getMonth()];
    day = now.getDate();
  }
  const dj = String(djName || "").trim();
  return `${dj ? `${dj} - ` : ""}${month} ${day} Setlist`;
}

interface Props {
  pendingCount: number;
  pendingTotal: number;
  pendingCurrency?: string | null;
  djName?: string;
  onClose: (delivered: boolean) => void;
  /** "Not delivering this set? Cancel the sale" — lives here, under the
      first step, not on the Balance card (founder, 9/02). Omitted → no line
      (an older server that returns no orderIds). */
  onCancelSale?: () => void;
}

const SetlistUploadFlow: React.FC<Props> = ({
  pendingCount,
  pendingTotal,
  pendingCurrency,
  djName,
  onClose,
  onCancelSale,
}) => {
  const [step, setStep] = useState<"pick" | "parsing" | "review" | "delivering" | "done">("pick");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const review = useSetlistReview(preview);
  const [setName, setSetName] = useState("");
  const [deliverError, setDeliverError] = useState<string | null>(null);
  const [result, setResult] = useState<DeliverResponse | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Phone → laptop hand-off: Serato writes the file on the computer, so the
  // phone-first DJ copies this link and opens it there (?upload=1 reopens
  // this flow on arrival). Clipboard API with the legacy fallback, same
  // recipe as TrackHistoryUploader.
  const uploadLink = publicUrl("/balance?upload=1");
  const copyUploadLink = () => {
    copyTextToClipboard(uploadLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Body scroll lock while the flow is up.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleFiles = (list: FileList | File[] | null) => {
    setFileError(null);
    if (!list || list.length === 0) return;
    const first = list[0];
    const ext = (first.name.toLowerCase().match(/\.[a-z0-9]+$/) || [""])[0];
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setFileError(tx("setlistUploadFlow.thatFileTypeIsn", { ACCEPTED_EXTENSIONS: ACCEPTED_EXTENSIONS.join(", ") }));
      return;
    }
    if (first.size > MAX_BYTES) {
      setFileError(tx("setlistUploadFlow.fileIsTooLarge", { formatBytes: formatBytes(first.size) }));
      return;
    }
    setFile(first);
  };

  const runPreview = async () => {
    if (!file || step === "parsing") return;
    setStep("parsing");
    setFileError(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const data = await setlistsApi.preview(fd);
      setPreview(data);
      review.reset();
      setSetName(buildDefaultName(data.session, djName));
      setStep("review");
    } catch (err: any) {
      setFileError(
        err?.response?.data?.msg || tx("setlistUploadFlow.weCouldnTProcess")
      );
      setStep("pick");
    }
  };

  const deliver = async () => {
    if (!preview || step === "delivering") return;
    setStep("delivering");
    setDeliverError(null);

    const tracks = review.finalTracks;

    try {
      const data = await setlistsApi.deliver({
        name: setName.trim() || buildDefaultName(preview.session, djName),
        session: preview.session,
        tracks,
      });
      setResult(data);
      setStep("done");
    } catch (err: any) {
      setDeliverError(
        err?.response?.data?.msg || tx("setlistUploadFlow.deliveryFailedYourSales")
      );
      setStep("review");
    }
  };

  const body = (
    <div className="suf-overlay" role="dialog" aria-modal="true" aria-label={tx("setlistUploadFlow.uploadTrackHistory2")}>
      <div className="suf-panel">
        {/* ── PICK ── */}
        {step === "pick" && (
          <>
            <div className="suf-head">
              <div className="suf-title">{tx("setlistUploadFlow.uploadTrackHistory")}</div>
              <button type="button" className="suf-close" onClick={() => onClose(false)} aria-label={tx("setlistUploadFlow.close")}>
                ✕
              </button>
            </div>
            {/* Same celebration as the Balance card (founder decision): the money
                in brand green. Free sales have no money to celebrate — say
                who is waiting instead. */}
            {pendingCount > 0 && (
              <div className="suf-context">
                <span className="suf-context-art" aria-hidden="true">
                  <SetlistSealedArt compact withMarks />
                </span>
                <span className="suf-context-text">
                  {pendingTotal > 0 ? (
                    <>
                      <T k="setlistUploadFlow.congratulationsWaiting" vars={{ amount: formatTxnAmount(pendingTotal, pendingCurrency) }} tags={{ 0: (c) => <span className="suf-context-amt">{c}</span> }} />
                    </>
                  ) : (
                    <>{tx("setlistUploadFlow.fansWaitingForSetlist", { count: pendingCount })}</>
                  )}
                </span>
              </div>
            )}
            <div
              className={`suf-drop${dragOver ? " is-drag" : ""}${file ? " suf-drop--captured" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFiles(e.dataTransfer?.files || null);
              }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {file ? (
                <div className="suf-drop-file">
                  <div className="suf-file-check" aria-hidden>✓</div>
                  <div className="suf-file-captured-title">{tx("setlistUploadFlow.fileCaptured")}</div>
                  <div className="suf-drop-filename">{file.name}</div>
                  <div className="suf-drop-filemeta">{tx("setlistUploadFlow.readyToMatch", { formatBytes: formatBytes(file.size) })}</div>
                </div>
              ) : (
                <>
                  {/* The composer's green icon (founder: "make
                      them coherent"). */}
                  <svg className="suf-drop-icon" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#24C961" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M12 16V4M7 9l5-5 5 5" />
                    <path d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
                  </svg>
                  <div className="suf-drop-title">{tx("setlistUploadFlow.uploadTrackHistory2")}</div>
                  <div className="suf-drop-sub">{tx("setlistUploadFlow.tapToBrowse")}</div>
                </>
              )}
            </div>
            {fileError && <div className="suf-error">{fileError}</div>}
            {/* The file is born on the laptop — hand the phone-first DJ a
                bridge (founder, 2026-08-21; an email with this link also
                lands the moment a sale is accepted). */}
            <div className="suf-laptop-link" aria-live="polite">
              <span className="suf-laptop-link-text">{tx("setlistUploadFlow.uploadFromLaptop")}</span>
              <button type="button" className="suf-laptop-link-btn" onClick={copyUploadLink}>
                {linkCopied ? tx("setlistUploadFlow.copied") : tx("setlistUploadFlow.copyLink")}
              </button>
            </div>
            <p className="suf-helper">
              <T k="setlistUploadFlow.exportFromSeratoHistory" tags={{ 0: (c) => <strong>{c}</strong>, 1: (c) => <strong>{c}</strong>, 2: (c) => <strong>{c}</strong>, 3: (c) => <strong>{c}</strong> }} />
            </p>
            <div className="suf-actions">
              {/* Cancel = cancel the SALE, behind the confirm sheet (founder,
                  9/02). The ✕ up top is the plain "close this". An older
                  server that sent no orderIds falls back to closing. */}
              <button type="button" className="suf-btn-ghost" onClick={onCancelSale ?? (() => onClose(false))}>
                {tx("setlistUploadFlow.cancel")}
              </button>
              {/* Founder call (reaffirmed): the money-forward label on
                  step one too. */}
              <button type="button" className="suf-btn-go" disabled={!file} onClick={runPreview}>
                {pendingTotal > 0 ? tx("setlistUploadFlow.claim", { amount: formatTxnAmount(pendingTotal, pendingCurrency) }) : tx("setlistUploadFlow.continue")}
              </button>
            </div>
          </>
        )}

        {/* ── PARSING ── */}
        {step === "parsing" && (
          <div className="suf-busy">
            <div className="suf-spinner" aria-hidden />
            <div className="suf-busy-title">{tx("setlistUploadFlow.matchingYourTracks")}</div>
            <div className="suf-busy-sub">{tx("setlistUploadFlow.longSetsTakeUp")}</div>
          </div>
        )}

        {/* ── REVIEW ── */}
        {step === "review" && preview && (
          <>
            <div className="suf-head">
              <div className="suf-title">{tx("setlistUploadFlow.reviewTheSet")}</div>
              <button type="button" className="suf-close" onClick={() => onClose(false)} aria-label={tx("setlistUploadFlow.close")}>
                ✕
              </button>
            </div>
            <div className="suf-count">{tx("setlistReview.tracksCount", { count: preview.tracks.length })}</div>

            <label className="suf-name-label" htmlFor="suf-name">
              {tx("setlistUploadFlow.setName")}
            </label>
            <input
              id="suf-name"
              className="suf-name-input"
              value={setName}
              maxLength={120}
              onChange={(e) => setSetName(e.target.value)}
            />

            <SetlistTrackReview review={review} />

            {deliverError && <div className="suf-error">{deliverError}</div>}

            <div className="suf-actions">
              <button type="button" className="suf-btn-ghost" onClick={() => setStep("pick")}>
                {tx("setlistUploadFlow.back")}
              </button>
              {/* The money moves HERE (capture + delivery), so this is where the
                  claim wording belongs (founder, 9/02). Free sales keep the
                  plain verb — nothing is claimed. */}
              <button type="button" className="suf-btn-go" onClick={deliver} disabled={review.finalTracks.length === 0}>
                {pendingTotal > 0
                  ? tx("setlistUploadFlow.claim", { amount: formatTxnAmount(pendingTotal, pendingCurrency) })
                  : tx("setlistUploadFlow.deliverTracks", { count: review.finalTracks.length })}
              </button>
            </div>
          </>
        )}

        {/* ── DELIVERING ── */}
        {step === "delivering" && (
          <div className="suf-busy">
            <div className="suf-spinner" aria-hidden />
            <div className="suf-busy-title">{tx("setlistUploadFlow.buildingThePlaylist")}</div>
            <div className="suf-busy-sub">{tx("setlistUploadFlow.collectingYourSalesAnd")}</div>
          </div>
        )}

        {/* ── DONE ── */}
        {step === "done" && result && (
          <div className="suf-done">
            <div className="suf-done-check" aria-hidden>
              ✓
            </div>
            <div className="suf-done-title">{tx("setlistUploadFlow.delivered")}</div>
            <div className="suf-done-sub">
              {tx("setlistUploadFlow.salesCollected", { count: result.delivered })} · {tx("setlistUploadFlow.fansEmailed", { count: result.emailed })}
            </div>
            {result.captureFailed > 0 && (
              <div className="suf-done-warn">
                {tx("setlistUploadFlow.paymentsNotCollected", { count: result.captureFailed })}
              </div>
            )}
            {result.spotifyPlaylistUrl && (
              <button
                type="button"
                className="suf-btn-spotify"
                onClick={() => openExternal(result.spotifyPlaylistUrl!)}
              >
                {tx("setlistUploadFlow.openTheSpotifyPlaylist")}
              </button>
            )}
            <button type="button" className="suf-btn-go suf-btn-done" onClick={() => onClose(true)}>
              {tx("setlistUploadFlow.done")}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(body, document.body);
};

export default SetlistUploadFlow;
